/**
 * Schedule Data Adapter — the SINGLE boundary between storage and scoring.
 *
 * This module reads from localStorage (via the Data layer) and passes
 * assembled data to the pure scoring functions in instanceScoring.ts.
 *
 * When the backend migration happens (PostgreSQL API), only this file
 * needs to change — the scoring module and chart components stay untouched.
 */

import {
  getScheduleProgressOverrideSeriesForInstance,
  getScheduleCurveMetaForInstance,
  type ScheduleProgressPoint,
  type ScheduleCurveMeta,
} from "../Data/ScheduleProgress/scheduleProgressOverrides";
import {
  getProjectKpiConfigOverride,
  type SubKpiInstance,
} from "../Data/ProjectConfig/projectKpiConfigOverrides";
import type { KpiSignal } from "./types";
import type { ScheduleToleranceConfig } from "./schedule";
import {
  computeScheduleCategoryFromInstances,
  type InstanceScore,
} from "./instanceScoring";

// ---------------------------------------------------------------------------
// Config reads
// ---------------------------------------------------------------------------

/**
 * Read the SubKpiInstance[] config for a project's Schedule category
 * from localStorage.
 */
export function getScheduleInstancesFromStorage(
  projectGuid: string,
): SubKpiInstance[] {
  const pg = String(projectGuid || "").trim();
  if (!pg) return [];
  const cfg = getProjectKpiConfigOverride(pg as any);
  return Array.isArray(cfg?.category_instances?.schedule)
    ? cfg!.category_instances!.schedule!
    : [];
}

// ---------------------------------------------------------------------------
// Data reads
// ---------------------------------------------------------------------------

/**
 * Read the progress data series for a single instance from localStorage.
 */
export function getInstanceSeriesFromStorage(
  projectGuid: string,
  instanceGuid?: string,
): ScheduleProgressPoint[] {
  return getScheduleProgressOverrideSeriesForInstance(projectGuid, instanceGuid);
}

/**
 * Read curve metadata for a single instance from localStorage.
 */
export function getInstanceCurveMetaFromStorage(
  projectGuid: string,
  instanceGuid?: string,
): ScheduleCurveMeta | null {
  return getScheduleCurveMetaForInstance(projectGuid, instanceGuid) ?? null;
}

/**
 * Build a Map<instanceGuid, ScheduleProgressPoint[]> for all instances
 * in a project's Schedule category. This is the data shape expected by
 * the pure computeScheduleCategoryFromInstances() function.
 */
export function buildSeriesMapFromStorage(
  projectGuid: string,
  instances: SubKpiInstance[],
): Map<string, ScheduleProgressPoint[]> {
  const map = new Map<string, ScheduleProgressPoint[]>();
  for (const inst of instances) {
    const key = String(inst.instance_guid || "").trim();
    const series = getScheduleProgressOverrideSeriesForInstance(
      projectGuid,
      key || undefined,
    );
    map.set(key, series);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Composite: read + score (convenience for App.tsx)
// ---------------------------------------------------------------------------

/**
 * Read config and data from localStorage, then run the pure scoring pipeline.
 *
 * Drop-in replacement for the old computeScheduleCategoryFromInstances()
 * that read storage internally. Same return type, same semantics.
 */
export function computeScheduleCategoryFromStorage(
  projectGuid: string,
  asOfDate: string,
  globalScheduleConfig: ScheduleToleranceConfig,
): { signal: KpiSignal; instanceScores: InstanceScore[] } | null {
  const instances = getScheduleInstancesFromStorage(projectGuid);
  if (instances.length === 0) return null;

  const seriesMap = buildSeriesMapFromStorage(projectGuid, instances);

  // Guard: if instance configs exist but NO series data has been loaded into
  // the store yet (hydration timing race), return null so the caller falls
  // through to the API-data scoring path or the bundle fallback.
  let hasAnyPoints = false;
  seriesMap.forEach((pts) => { if (pts.length > 0) hasAnyPoints = true; });
  if (!hasAnyPoints) return null;

  return computeScheduleCategoryFromInstances(
    projectGuid,
    asOfDate,
    globalScheduleConfig,
    instances,
    seriesMap,
  );
}

// ---------------------------------------------------------------------------
// Data-driven: pre-fetched instances + series (no localStorage reads)
// ---------------------------------------------------------------------------

/**
 * Score a project's Schedule category using PRE-FETCHED data from the API.
 *
 * This is the API-first path. When the API provides KPI config instances for a
 * project, App.tsx calls this instead of computeScheduleCategoryFromStorage().
 * The pure scoring function (computeScheduleCategoryFromInstances) is the same;
 * only the data source changes.
 *
 * @param projectGuid     The project being scored.
 * @param asOfDate        Reporting cutoff (YYYY-MM-DD).
 * @param globalConfig    Platform-wide schedule thresholds (kpiConfig.schedule).
 * @param instances       Pre-fetched SubKpiInstance[] from the API (already in
 *                        seed shape via kpiConfigToSubKpiInstance).
 * @param allScheduleData All schedule points in seed shape (project_GUID, etc.).
 *                        The function filters to this project + each instance.
 */
export function computeScheduleCategoryFromData(
  projectGuid: string,
  asOfDate: string,
  globalConfig: ScheduleToleranceConfig,
  instances: SubKpiInstance[],
  allScheduleData: Record<string, any>[],
): { signal: KpiSignal; instanceScores: InstanceScore[] } | null {
  if (!instances.length) return null;

  // Build seriesMap from the pre-fetched flat array.
  // Case-insensitive GUID comparison (CLAUDE.md convention).
  const pgUpper = projectGuid.toUpperCase();
  const projectPoints = allScheduleData.filter(
    (r) => String(r.project_GUID || "").trim().toUpperCase() === pgUpper
  );

  const seriesMap = new Map<string, ScheduleProgressPoint[]>();
  for (const inst of instances) {
    const iGuid = String(inst.instance_guid || "").trim();
    // Look for instance-specific points first
    const instPoints: ScheduleProgressPoint[] = projectPoints
      .filter((r) => String(r.instance_guid || "").trim() === iGuid)
      .map((r) => ({
        project_guid: projectGuid,
        instance_guid: iGuid || undefined,
        reporting_date: String(r.reporting_date || ""),
        baseline_complete: r.baseline_complete ?? null,
        actual_complete: r.actual_complete ?? null,
        forecast_complete: r.forecast_complete ?? null,
      }));

    if (instPoints.length > 0) {
      seriesMap.set(iGuid, instPoints);
    } else {
      // Fallback: use project-level points (instance_guid is empty/undefined)
      const projLevel: ScheduleProgressPoint[] = projectPoints
        .filter((r) => !String(r.instance_guid || "").trim())
        .map((r) => ({
          project_guid: projectGuid,
          instance_guid: undefined,
          reporting_date: String(r.reporting_date || ""),
          baseline_complete: r.baseline_complete ?? null,
          actual_complete: r.actual_complete ?? null,
          forecast_complete: r.forecast_complete ?? null,
        }));
      seriesMap.set(iGuid, projLevel);
    }
  }

  return computeScheduleCategoryFromInstances(
    projectGuid,
    asOfDate,
    globalConfig,
    instances,
    seriesMap,
  );
}

// ---------------------------------------------------------------------------
// Chart data bundle (for passing pre-fetched data to chart components)
// ---------------------------------------------------------------------------

export type InstanceChartData = {
  series: ScheduleProgressPoint[];
  curveMeta: ScheduleCurveMeta | null;
};

/**
 * Read all data needed by a chart component for a single instance.
 * Charts that receive this as a prop can skip their own localStorage reads.
 */
export function getInstanceChartDataFromStorage(
  projectGuid: string,
  instanceGuid?: string,
): InstanceChartData {
  return {
    series: getInstanceSeriesFromStorage(projectGuid, instanceGuid),
    curveMeta: getInstanceCurveMetaFromStorage(projectGuid, instanceGuid),
  };
}
